import React, { useMemo, useState } from "react";
import { api } from "../../../services/api";
import { useQuery } from "@tanstack/react-query";
import { useUser } from "../../../services/user-context";

export const ReportsContext = React.createContext({});

// used to fetch all the certifications, users and other data
export function ReportsProvider({ children }) {
  const { userInfo } = useUser() as any;
  const [selectedReports, setSelectedReports] = useState<any>(null);
  const { data: userData } = useQuery({
    queryKey: ["search-users"],
    queryFn: () => api.searchUsers(),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const userId = userInfo?.id || userInfo?.["sub"];

  // Fetch FIRST PAGE ONLY of reports for preview components (SessionsCard, etc.)
  // Components needing full list should use their own pagination (see SessionLogs with enablePagination)
  // Use userId only in key so tutorial/other userInfo updates don't refetch and cause Session Log to refresh
  const { data: reportsPreviewData, isLoading: isLoadingReports } = useQuery({
    queryKey: ["reports-preview", userId],
    queryFn: () => api.getAllReports(undefined),
    enabled: !!userId,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const {
    data: praxisScores,
  } = useQuery({
    queryKey: ["praxis-scores-reports"],
    queryFn: () => api.getPraxisScores(),
    refetchOnWindowFocus: false,
  });

  const { data: detailedReportData, isLoading: isLoadingDetailedReport } =
    useQuery({
      queryKey: ["detailed-report", selectedReports?.sessionID],
      queryFn: () => api.getDetailedReport(selectedReports?.sessionID),
      enabled: !!selectedReports?.sessionID,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    });

  const users = userData?.users || [];


  // Preview data - first page only. For full paginated list, use SessionLogs with enablePagination
  const userReports = useMemo(() => {
    if (!reportsPreviewData?.data?.data) return [];
    
    return reportsPreviewData.data.data;
  }, [reportsPreviewData]);

  const reportAverage = useMemo(() => {
    return  praxisScores?.data || {};
  }, [praxisScores]);

  const values = {
    users,
    selectedReports,
    setSelectedReports,
    userReports,
    isLoadingReports,
    reportAverage,
    detailedReport: detailedReportData?.data ? {
      ...detailedReportData?.data,
      session: selectedReports,
    } : undefined,
    isLoadingDetailedReport,
  };
  return (
    <ReportsContext.Provider value={values}>{children}</ReportsContext.Provider>
  );
}

export function useReports(): {
  userReports: any;
  isLoadingReports: boolean;
  setCertifications: React.Dispatch<React.SetStateAction<any>>;
  users: any;
  setUsers: React.Dispatch<React.SetStateAction<any>>;
  selectedReports: any;
  setSelectedReports: React.Dispatch<React.SetStateAction<any>>;
  reportAverage: any;
  detailedReport: any;
  isLoadingDetailedReport: boolean;
} {
  const context = React.useContext(ReportsContext);
  if (context === undefined) {
    throw new Error("useReports must be used within a ReportsProvider");
  }
  return context as {
    userReports: any;
    isLoadingReports: boolean;
    setCertifications: React.Dispatch<React.SetStateAction<any>>;
    users: any;
    setUsers: React.Dispatch<React.SetStateAction<any>>;
    selectedReports: any;
    setSelectedReports: React.Dispatch<React.SetStateAction<any>>;
    reportAverage: any;
    detailedReport: any;
    isLoadingDetailedReport: boolean;
  };
}
